import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Table, Button, Input, Modal, Form, Tag, Space, Tabs, Badge, Alert, Tooltip } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined } from '@ant-design/icons'
import { apiRequest } from '../../helpers'
import EmailSourceButton from './EmailSourceButton'

const apiUrl = process.env.REACT_APP_API

const monedaColor = {
  RON: 'green',
  EUR: 'blue',
  USD: 'cyan',
}

const formatPret = (pret, moneda) => {
  if (pret == null) return <span style={{ color: '#aaa' }}>—</span>

  return (
    <>
      <strong>{Number(pret).toLocaleString('ro-RO', { minimumFractionDigits: 2 })}</strong>{' '}
      <Tag color={monedaColor[moneda] || 'default'} style={{ fontSize: 10, marginLeft: 4 }}>
        {moneda}
      </Tag>
    </>
  )
}

const cardStyle = {
  background: '#fff',
  borderRadius: 8,
  padding: 16,
  boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
}

const TpmParts = () => {
  const [parts, setParts] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [meta, setMeta] = useState({ totalItems: 0 })

  const [selectedPart, setSelectedPart] = useState(null)
  const [selectedPartKey, setSelectedPartKey] = useState(null)

  const [offers, setOffers] = useState([])
  const [rfqs, setRfqs] = useState([])
  const [loadingDetail, setLoadingDetail] = useState(false)

  const [offerPage, setOfferPage] = useState(1)
  const [offerPageSize, setOfferPageSize] = useState(20)
  const [offerMeta, setOfferMeta] = useState({ total: 0 })

  const [rfqPage, setRfqPage] = useState(1)
  const [rfqPageSize, setRfqPageSize] = useState(20)
  const [rfqMeta, setrfqMeta] = useState({ total: 0 })

  const [modalOpen, setModalOpen] = useState(false)
  const [eanModalOpen, setEanModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [editPart, setEditPart] = useState(null)

  const [form] = Form.useForm()
  const [eanForm] = Form.useForm()
  const [saving, setSaving] = useState(false)
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [bulkDeleting, setBulkDeleting] = useState(false)

  const fetchParts = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(pageSize),
        ...(search ? { search } : {}),
        sortBy: 'createdAt:ASC',
      })

      const res = await apiRequest(`${apiUrl}tpm/parts?${params}`, 'GET')
      const rows = res?.data?.data || []

      setParts(rows)
      setMeta({ totalItems: res?.data?.meta?.totalItems || 0 })

      if (selectedPartKey) {
        const refreshedSelectedPart = rows.find(part => part.partNumber === selectedPartKey) || null
        setSelectedPart(refreshedSelectedPart)

        if (!refreshedSelectedPart) {
          setSelectedPartKey(null)
          setOffers([])
          setRfqs([])
          setOfferMeta({ total: 0 })
        }
      }
    } catch {
      setError('Eroare la incarcarea pieselor.')
      setParts([])
      setMeta({ totalItems: 0 })
    } finally {
      setLoading(false)
    }
  }, [page, pageSize, search, selectedPartKey])

  useEffect(() => {
    fetchParts()
  }, [fetchParts])

  const fetchOffers = useCallback(
    async partNumber => {
      if (!partNumber) return

      try {
        const params = new URLSearchParams({
          page: String(offerPage),
          limit: String(offerPageSize),
          'filter.partNumber': partNumber,
          sortBy: 'dataOferta:DESC',
        })

        const res = await apiRequest(`${apiUrl}tpm/offers?${params}`, 'GET')
        setOffers(res?.data?.data || [])
        setOfferMeta({ total: res?.data?.meta?.totalItems || 0 })
      } catch {
        setOffers([])
        setOfferMeta({ total: 0 })
      }
    },
    [offerPage, offerPageSize]
  )
  
  const fetchRfqs = useCallback(
    async partNumber => {
      if (!partNumber) return
      try {
        const params = new URLSearchParams({
          page: String(rfqPage),
          limit: String(rfqPageSize),
          'filter.partNumber': partNumber,
          sortBy: 'createdAt:DESC',
        })
        const res = await apiRequest(`${apiUrl}tpm/rfq-items?${params}`, 'GET')
        setRfqs(res?.data?.data || [])
        setrfqMeta({ total: res?.data?.meta?.totalItems || 0 })
      } catch {
        setRfqs([])
      }
    },
    [rfqPage, rfqPageSize]
  )
  const fetchDetail = useCallback(
    async partNumber => {
      if (!partNumber) return

      setLoadingDetail(true)
      try {
        await Promise.all([fetchOffers(partNumber), fetchRfqs(partNumber)])
      } finally {
        setLoadingDetail(false)
      }
    },
    [fetchOffers, fetchRfqs]
  )

  useEffect(() => {
    if (selectedPart?.partNumber) {
      fetchOffers(selectedPart.partNumber)
    }
  }, [selectedPart?.partNumber, offerPage, offerPageSize, fetchOffers])

  useEffect(() => {
    if (selectedPart?.partNumber) {
      fetchRfqs(selectedPart.partNumber)
    }
  }, [selectedPart?.partNumber, rfqPage, rfqPageSize, fetchRfqs])

  const handleSelectPart = useCallback(
    part => {
      setSelectedPart(part)
      setSelectedPartKey(part.partNumber)
      setOfferPage(1)
      setRfqPage(1)
      fetchDetail(part.partNumber)
    },
    [fetchDetail]
  )

  const openCreate = () => {
    setEditPart(null)
    form.resetFields()
    setModalOpen(true)
  }

  const openEdit = (part, e) => {
    e.stopPropagation()
    setEditPart(part)
    form.setFieldsValue({
      partNumber: part.partNumber,
      denumire: part.denumire,
      brand: part.brand || '',
    })
    setModalOpen(true)
  }

  const openDelete = (part, e) => {
    e.stopPropagation()
    setEditPart(part)
    setDeleteModalOpen(true)
  }

  const openEan = (part, e) => {
    e.stopPropagation()
    setEditPart(part)
    eanForm.resetFields()
    setEanModalOpen(true)
  }

  const handleSave = async () => {
    const values = await form.validateFields()
    setSaving(true)

    try {
      if (editPart) {
        await apiRequest(`${apiUrl}tpm/parts/${encodeURIComponent(editPart.partNumber)}`, 'PATCH', values)
      } else {
        await apiRequest(`${apiUrl}tpm/parts`, 'POST', values)
      }

      setModalOpen(false)
      await fetchParts()

      const keyToSelect = values.partNumber || editPart?.partNumber
      if (keyToSelect) {
        setSelectedPartKey(keyToSelect)
      }
    } catch (e) {
      Modal.error({
        title: 'Eroare',
        content: e?.response?.data?.message || 'Eroare la salvare.',
      })
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!editPart?.partNumber) return

    try {
      await apiRequest(`${apiUrl}tpm/parts/${encodeURIComponent(editPart.partNumber)}`, 'DELETE')
      setDeleteModalOpen(false)

      if (selectedPartKey === editPart.partNumber) {
        setSelectedPart(null)
        setSelectedPartKey(null)
        setOffers([])
        setRfqs([])
        setOfferMeta({ total: 0 })
      }

      await fetchParts()
    } catch {
      Modal.error({
        title: 'Eroare',
        content: 'Eroare la stergere.',
      })
    }
  }

  const handleAddEAN = async () => {
    const values = await eanForm.validateFields()
    setSaving(true)

    try {
      await apiRequest(`${apiUrl}tpm/parts/ean`, 'POST', {
        ...values,
        partNumber: editPart.partNumber,
      })

      setEanModalOpen(false)
      await fetchParts()
    } catch (e) {
      Modal.error({
        title: 'Eroare',
        content: e?.response?.data?.message || 'Eroare la adaugarea EAN.',
      })
    } finally {
      setSaving(false)
    }
  }

  const handleBulkDelete = () => {
    if (selectedRowKeys.length === 0) return
    Modal.confirm({
      title: `Stergi ${selectedRowKeys.length} piese?`,
      content: 'Se vor sterge si toate ofertele asociate. Aceasta actiune nu poate fi anulata.',
      okText: 'Sterge',
      okButtonProps: { danger: true },
      cancelText: 'Anuleaza',
      onOk: async () => {
        setBulkDeleting(true)
        let errors = 0
        for (const partNumber of selectedRowKeys) {
          try {
            await apiRequest(`${apiUrl}tpm/parts/${encodeURIComponent(partNumber)}`, 'DELETE')
            if (selectedPartKey === partNumber) {
              setSelectedPart(null)
              setSelectedPartKey(null)
              setOffers([])
              setRfqs([])
              setOfferMeta({ total: 0 })
            }
          } catch { errors++ }
        }
        setSelectedRowKeys([])
        setBulkDeleting(false)
        await fetchParts()
        if (errors > 0) {
          Modal.error({ title: 'Eroare', content: `${errors} piese nu au putut fi sterse.` })
        }
      },
    })
  }

  const handleDeleteEAN = async (eanId, e) => {
    e.stopPropagation()

    try {
      await apiRequest(`${apiUrl}tpm/parts/ean/${eanId}`, 'DELETE')
      await fetchParts()
    } catch {
      Modal.error({
        title: 'Eroare',
        content: 'Eroare la stergerea EAN.',
      })
    }
  }

  const partsColumns = useMemo(
    () => [
      {
        title: 'Part Number',
        dataIndex: 'partNumber',
        key: 'partNumber',
       // render: value => <code>{value}</code>,
      },
      {
        title: 'Denumire',
        dataIndex: 'denumire',
        key: 'denumire',
      },
      {
        title: 'Brand',
        dataIndex: 'brand',
        key: 'brand',
        render: value => value || <span style={{ color: '#aaa' }}>—</span>,
      },
      {
        title: 'Coduri EAN',
        key: 'eans',
        render: (_, record) => (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }} onClick={e => e.stopPropagation()}>
            {(record.eans || []).map(ean => (
              <Tag key={ean.id} closable onClose={async (e) => { await handleDeleteEAN(ean.id, e) }}>
                {ean.codEAN}
              </Tag>
            ))}
            <Tag color="blue" style={{ cursor: 'pointer' }} onClick={e => openEan(record, e)}>
              + EAN
            </Tag>
          </div>
        ),
      },
      {
        title: 'Actiuni',
        key: 'actions',
        width: 100,
        render: (_, record) => (
          <Space onClick={e => e.stopPropagation()}>
            <Tooltip title="Editeaza">
              <Button size="small" icon={<EditOutlined />} onClick={e => openEdit(record, e)} />
            </Tooltip>
            <Tooltip title="Sterge">
              <Button size="small" danger icon={<DeleteOutlined />} onClick={e => openDelete(record, e)} />
            </Tooltip>
          </Space>
        ),
      },
    ],
    []
  )

  const offerColumns = useMemo(
    () => [
      {
        title: 'Firma',
        dataIndex: 'firma',
        key: 'firma',
        render: value => value || <span style={{ color: '#aaa' }}>—</span>,
      },
      {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
        render: value => (value ? <a href={`mailto:${value}`}>{value}</a> : <span style={{ color: '#aaa' }}>—</span>),
      },
      {
        title: 'Pret',
        key: 'pret',
        render: (_, record) => formatPret(record.pret, record.moneda),
      },
      {
        title: 'Cantitate',
        dataIndex: 'cantitate',
        key: 'cantitate',
        render: value => value ?? <span style={{ color: '#aaa' }}>—</span>,
      },
      {
        title: 'Data oferta',
        dataIndex: 'dataOferta',
        key: 'dataOferta',
        render: value => (value ? new Date(value).toLocaleDateString('ro-RO') : <span style={{ color: '#aaa' }}>—</span>),
      },
      {
        title: 'Email sursa',
        key: 'emailMessageId',
        render: (_, record) => <EmailSourceButton emailMessageId={record.emailMessageId} />,
      },
    ],
    []
  )

  const rfqColumns = useMemo(
    () => [
      {
        title: 'Firma',
        dataIndex: 'firma',
        key: 'firma',
        render: value => value || <span style={{ color: '#aaa' }}>—</span>,
      },
      {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
        render: value => (value ? <a href={`mailto:${value}`}>{value}</a> : <span style={{ color: '#aaa' }}>—</span>),
      },
      {
        title: 'Pret',
        key: 'pret',
        render: (_, record) => formatPret(record.pret, record.moneda),
      },
      {
        title: 'Cantitate',
        dataIndex: 'cantitate',
        key: 'cantitate',
        render: value => value ?? <span style={{ color: '#aaa' }}>—</span>,
      },
      {
        title: 'Data RFQ',
        dataIndex: 'dataOferta',
        key: 'dataOferta',
        render: value => (value ? new Date(value).toLocaleDateString('ro-RO') : <span style={{ color: '#aaa' }}>—</span>),
      },
      {
        title: 'Email sursa',
        key: 'emailMessageId',
        render: (_, record) => <EmailSourceButton emailMessageId={record.emailMessageId} />,
      },
    ],
    []
  )

  const detailTabs = useMemo(
    () => [
      {
        key: 'offers',
        label: (
          <span>
            Oferte <Badge count={offerMeta.total} color="green" />
          </span>
        ),
        children: (
          <Table
            columns={offerColumns}
            dataSource={offers}
            rowKey="id"
            size="small"
            loading={loadingDetail}
            scroll={{ y: 240 }}
            pagination={{
              current: offerPage,
              pageSize: offerPageSize,
              total: offerMeta.total,
              onChange: (p, ps) => {
                setOfferPage(p)
                setOfferPageSize(ps)
              },
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 50],
            }}
            locale={{ emptyText: 'Nu exista oferte pentru aceasta piesa.' }}
          />
        ),
      },
      {
        key: 'rfq',
        label: (
          <span>
            Cereri de oferta <Badge count={rfqs.length} color="blue" />
          </span>
        ),
        children: (
          <Table
            columns={rfqColumns}
            dataSource={rfqs}
            rowKey={(r, i) => r.id || i}
            size="small"
            loading={loadingDetail}
            scroll={{ y: 240 }}
            pagination={{
              current: rfqPage,
              pageSize: rfqPageSize,
              total: rfqs.length,
              onChange: (p, ps) => {
                setRfqPage(p)
                setRfqPageSize(ps)
              },
              showSizeChanger: true,
            }}
            locale={{ emptyText: 'Nu exista cereri de oferta pentru aceasta piesa.' }}
          />
        ),
      },
    ],
    [offerColumns, offers, loadingDetail, offerMeta.total, offerPage, offerPageSize, rfqColumns, rfqs, rfqPage, rfqPageSize]
  )

  return (
    <div className="tpm-parts-page">
      <style>{`
        .tpm-parts-page .selectable-table .ant-table-tbody > tr.selected-row > td {
          background-color: var(--cui-primary, #dbeafe) !important;
        }

        .tpm-parts-page .selectable-table .ant-table-tbody > tr.selected-row:hover > td {
          background-color: var(--cui-primary, #dbeafe) !important;
          filter: brightness(0.98);
        }

        .tpm-parts-page .selectable-table .ant-table-tbody > tr {
          cursor: pointer;
        }
      `}</style>

      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <strong style={{ fontSize: 16 }}>Catalog Piese</strong>

          <Space wrap>
            <Input.Search
              placeholder="Cauta part number, denumire, brand..."
              value={search}
              onChange={e => {
                setSearch(e.target.value)
                setPage(1)
              }}
              style={{ width: 300 }}
              allowClear
            />
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Adauga piesa
            </Button>
            {selectedRowKeys.length > 0 && (
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={handleBulkDelete}
                loading={bulkDeleting}
              >
                Sterge {selectedRowKeys.length} piese
              </Button>
            )}
            <Button icon={<ReloadOutlined />} onClick={fetchParts} />
          </Space>
        </div>

        {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 12 }} />}

        <Table
          className="dyn-table selectable selectable-table"
          columns={partsColumns}
          dataSource={parts}
          rowKey="partNumber"
          loading={loading}
          size="small"
          scroll={{ y: 380 }}
          rowClassName={record => (record.partNumber === selectedPartKey ? 'selected-row' : '')}
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys),
            preserveSelectedRowKeys: true,
          }}
          onRow={record => ({
            onClick: () => handleSelectPart(record),
          })}
          pagination={{
            current: page,
            pageSize,
            total: meta.totalItems,
            onChange: (p, ps) => {
              setPage(p)
              setPageSize(ps)
            },
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
            showTotal: total => `Total: ${total} piese`,
          }}
          locale={{ emptyText: 'Nu exista piese.' }}
        />
      </div>

      {selectedPart && (
        <div style={cardStyle}>
          <div style={{ marginBottom: 12 }}>
            <strong>{selectedPart.partNumber}</strong>
            <span style={{ color: '#666', marginLeft: 8 }}>{selectedPart.denumire}</span>
            {selectedPart.brand && <Tag style={{ marginLeft: 8 }}>{selectedPart.brand}</Tag>}
          </div>

          <Tabs items={detailTabs} size="small" />
        </div>
      )}

      <Modal
        title={editPart ? 'Editeaza piesa' : 'Adauga piesa noua'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        confirmLoading={saving}
        okText={saving ? 'Se salveaza...' : 'Salveaza'}
        cancelText="Anuleaza"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Part Number"
            name="partNumber"
            rules={[{ required: true, message: 'Part Number este obligatoriu' }]}
          >
            <Input disabled={!!editPart} placeholder="ex: 0123, ABC456" />
          </Form.Item>

          {!editPart && (
            <small style={{ color: '#888', display: 'block', marginTop: -16, marginBottom: 8 }}>
              Zero-urile initiale sunt pastrate
            </small>
          )}

          <Form.Item
            label="Denumire"
            name="denumire"
            rules={[{ required: true, message: 'Denumirea este obligatorie' }]}
          >
            <Input placeholder="Denumirea piesei" />
          </Form.Item>

          <Form.Item label="Brand" name="brand">
            <Input placeholder="Brand / Producator (optional)" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Adauga cod EAN pentru ${editPart?.partNumber || ''}`}
        open={eanModalOpen}
        onOk={handleAddEAN}
        onCancel={() => setEanModalOpen(false)}
        confirmLoading={saving}
        okText="Adauga EAN"
        cancelText="Anuleaza"
      >
        <Form form={eanForm} layout="vertical">
          <Form.Item
            label="Cod EAN"
            name="codEAN"
            rules={[{ required: true, message: 'Codul EAN este obligatoriu' }]}
          >
            <Input placeholder="ex: 0123456789012" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Confirmare stergere"
        open={deleteModalOpen}
        onOk={handleDelete}
        onCancel={() => setDeleteModalOpen(false)}
        okText="Sterge"
        okButtonProps={{ danger: true }}
        cancelText="Anuleaza"
      >
        <p>
          Esti sigur ca vrei sa stergi piesa <strong>{editPart?.partNumber} - {editPart?.denumire}</strong>?
        </p>
        <p style={{ color: 'red', fontSize: 12 }}>Se vor sterge si toate ofertele asociate.</p>
      </Modal>
    </div>
  )
}

export default TpmParts